import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Table, { TableControls, TableFooter, TableHeading } from "@/components/Table";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { Path, useNavigate } from "@/router";
import { COMMITTEE_PEOPLE_SEARCH_PARAM } from "../constants";
import { UPDATE_BACKOFFICE_COMMITTEE_ASSIGNMENT_MUTATION } from "../mutations";
import {
  BACKOFFICE_COMMITTEE_ASSIGNMENTS_QUERY,
  BACKOFFICE_COMMITTEE_PARTICIPANTS_QUERY,
  BACKOFFICE_COMMITTEE_QUERY
} from "../queries";
import {
  DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY,
  type BackofficeCommitteeAssignmentsQueryResponse,
  type BackofficeCommitteeAssignmentsQueryVariables,
  type CommitteeAssignmentOrderByInput,
  type CommitteeAssignmentStatus,
  type CommitteeAssignmentWithCommittee,
  type CommitteeParticipationFilter
} from "../types";
import EditCommitteeAssignmentDialog, { type EditCommitteeAssignmentSaveInput } from "./EditCommitteeAssignmentDialog";
import {
  ASSIGNMENT_SORT_FIELD_MAP,
  canViewCommitteeAssignmentPerson,
  getCommitteeAssignmentDetailsColumns
} from "./TableColumns";

const PARTICIPATION_HEADING: Record<CommitteeParticipationFilter, { heading: string; body: string }> = {
  all: {
    heading: "All Participations",
    body: "View everyone assigned to this committee, including active and inactive terms."
  },
  active: {
    heading: "Active Participations",
    body: "View people currently serving on this committee."
  },
  inactive: {
    heading: "Inactive Participations",
    body: "View people whose committee terms are inactive or have ended."
  }
};

type CommitteeParticipantsTableProps = {
  committeeId: string;
  committeeName: string;
  participation: CommitteeParticipationFilter;
  canManageCommittees: boolean;
};

export default function CommitteeParticipantsTable({
  committeeId,
  committeeName,
  participation,
  canManageCommittees
}: CommitteeParticipantsTableProps) {
  const [selectedParticipantPgaId, setSelectedParticipantPgaId] = useState<string | null>(null);
  const [editingAssignment, setEditingAssignment] = useState<CommitteeAssignmentWithCommittee | null>(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: "endDate", desc: true }]);
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: COMMITTEE_PEOPLE_SEARCH_PARAM
  });
  const [settledSearch, setSettledSearch] = useState(debouncedSearch);
  const { isCompactLayout } = useIsCompactLayout();
  const navigate = useNavigate();
  const statusFilter: CommitteeAssignmentStatus | undefined =
    participation === "all" ? undefined : participation === "active" ? "ACTIVE" : "INACTIVE";

  useEffect(() => {
    resetPagination();
  }, [committeeId, participation, debouncedSearch, resetPagination]);

  const orderBy = useMemo((): CommitteeAssignmentOrderByInput => {
    const sort = sorting[0];
    if (!sort) return DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY;

    const field = ASSIGNMENT_SORT_FIELD_MAP[sort.id];
    if (!field) return DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY;

    return {
      field,
      direction: sort.desc ? "DESC" : "ASC"
    };
  }, [sorting]);

  const variables: BackofficeCommitteeAssignmentsQueryVariables = {
    id: committeeId,
    ...paginationVariables,
    ...(statusFilter && { status: statusFilter }),
    ...(debouncedSearch && { search: debouncedSearch }),
    orderBy
  };
  const participantsQuery = debouncedSearch
    ? BACKOFFICE_COMMITTEE_PARTICIPANTS_QUERY
    : BACKOFFICE_COMMITTEE_ASSIGNMENTS_QUERY;

  const { data, previousData, loading, error, refetch } = useQuery<
    BackofficeCommitteeAssignmentsQueryResponse,
    BackofficeCommitteeAssignmentsQueryVariables
  >(participantsQuery, {
    variables,
    skip: !committeeId,
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (!loading) {
      setSettledSearch(debouncedSearch);
    }
  }, [debouncedSearch, loading]);

  const [updateAssignment, { loading: updatingAssignment }] = useMutation(
    UPDATE_BACKOFFICE_COMMITTEE_ASSIGNMENT_MUTATION,
    {
      refetchQueries: [participantsQuery, BACKOFFICE_COMMITTEE_QUERY],
      update(cache) {
        const committeeCacheId = cache.identify({ __typename: "BackofficeCommittee", id: committeeId });
        if (!committeeCacheId) return;
        cache.evict({ id: committeeCacheId, fieldName: "assignments" });
      }
    }
  );

  const searchPending = debouncedSearch !== settledSearch;
  const connection = searchPending
    ? undefined
    : data?.backofficeCommittee?.id === committeeId
      ? data.backofficeCommittee.assignments
      : previousData?.backofficeCommittee?.id === committeeId
        ? previousData.backofficeCommittee.assignments
        : undefined;
  const rows = useMemo(
    (): CommitteeAssignmentWithCommittee[] =>
      (connection?.nodes ?? []).map((assignment) => ({
        ...assignment,
        committeeName
      })),
    [committeeName, connection?.nodes]
  );

  const handleViewParticipant = useCallback((participant: CommitteeAssignmentWithCommittee) => {
    if (!canViewCommitteeAssignmentPerson(participant)) return;
    setSelectedParticipantPgaId(participant.person?.pgaId ?? null);
  }, []);

  const handleEditParticipant = useCallback((participant: CommitteeAssignmentWithCommittee) => {
    setEditingAssignment(participant);
    setAssignmentDialogOpen(true);
  }, []);

  const handleAssignmentDialogClosed = useCallback(() => {
    setAssignmentDialogOpen(false);
    setEditingAssignment(null);
  }, []);

  const requestCloseAssignmentDialog = useCallback(() => {
    setAssignmentDialogOpen(false);
  }, []);

  const handleAssignmentDialogSave = useCallback(
    async (input: EditCommitteeAssignmentSaveInput) => {
      try {
        const { data: mutationData } = await updateAssignment({
          variables: {
            input: {
              id: input.id,
              titleId: input.titleId,
              startDate: input.startDate,
              endDate: input.endDate
            }
          }
        });

        const result = mutationData?.updateBackofficeCommitteeAssignment;
        if (!result?.success) {
          const apiMessage = result?.message;
          if (apiMessage) {
            showErrorSnackbar("Participation not updated", { subtitle: apiMessage });
          } else {
            showErrorSnackbar("Participation not updated", { subtitle: "Please try again later." });
          }
          return;
        }

        showSnackbar("Participation updated");
        requestCloseAssignmentDialog();
      } catch {
        showErrorSnackbar("Participation not updated", { subtitle: "Please try again later." });
      }
    },
    [requestCloseAssignmentDialog, updateAssignment]
  );

  const columns = useMemo(
    () =>
      getCommitteeAssignmentDetailsColumns(isCompactLayout, handleViewParticipant, {
        canManage: canManageCommittees,
        onEditParticipant: handleEditParticipant
      }),
    [canManageCommittees, handleEditParticipant, handleViewParticipant, isCompactLayout]
  );

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((currentSorting) =>
      typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue
    );
    resetPagination();
  };

  const heading = PARTICIPATION_HEADING[participation];
  const totalRowCount = connection?.totalCount ?? rows.length;
  const peopleSearchInput = (
    <SearchInput
      query={searchValue}
      onChange={(value) => {
        setSearchValue(value);
        resetPagination();
      }}
      onClear={() => {
        clearSearch();
        resetPagination();
      }}
      placeholder="Search People"
    />
  );

  if (error) {
    return (
      <>
        <TableHeading heading={heading.heading} body={heading.body} />
        <TableControls>{peopleSearchInput}</TableControls>
        <EmptyState
          title="Unable to Load Participations"
          caption={error.message}
          iconName="error"
          action={{ children: "Retry", onClick: () => void refetch() }}
        />
      </>
    );
  }

  return (
    <>
      <TableHeading heading={heading.heading} body={heading.body} />
      <Table
        data={rows}
        columns={columns}
        loading={searchPending || (loading && !connection)}
        sortingState={sorting}
        onSortingChange={handleSortingChange}
        serverSideSorting
        onClickRow={(row) => {
          if (canViewCommitteeAssignmentPerson(row.original)) {
            handleViewParticipant(row.original);
          }
        }}
        getRowClasses={(row) => (canViewCommitteeAssignmentPerson(row.original) ? "" : "cursor-default")}
        data-testid="committee-participants-table"
        hasActiveFilters={!!debouncedSearch}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Participations"
            caption="People added to this committee will appear here."
            iconName="groups"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No People Found" caption="Try a different name or PGA ID." iconName="search" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(connection, {
              loading,
              pageCount: rows.length,
              totalCount: totalRowCount
            })}
            onSortClick={onSortClick}
          >
            {peopleSearchInput}
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, {
              loading,
              pageCount: rows.length,
              totalCount: totalRowCount
            })}
            totalRowCount={totalRowCount}
          />
        )}
      />
      <PersonSideSheet
        open={!!selectedParticipantPgaId}
        onClose={() => setSelectedParticipantPgaId(null)}
        pgaId={selectedParticipantPgaId}
        customActionButtons={[
          {
            text: "View Committee History",
            variant: "outlined",
            onClick: () => {
              if (!selectedParticipantPgaId) return;
              navigate(`/people/${selectedParticipantPgaId}/membership/committees` as Path);
            }
          }
        ]}
      />
      <EditCommitteeAssignmentDialog
        open={assignmentDialogOpen}
        member={
          editingAssignment
            ? {
                id: editingAssignment.id,
                startDate: editingAssignment.startDate,
                endDate: editingAssignment.endDate,
                title: editingAssignment.title,
                person: editingAssignment.person,
                supportingText: editingAssignment.committeeName
              }
            : null
        }
        saving={updatingAssignment}
        onClose={handleAssignmentDialogClosed}
        onSave={handleAssignmentDialogSave}
      />
    </>
  );
}
